import { useState, useEffect } from 'react';
import api from '../../services/api';
import { Edit2, Trash2, Plus, ToggleLeft, ToggleRight, Info } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import ConfirmModal from '../../components/ConfirmModal';
import Loading from '../../components/Loading';

export default function ManageCoupons() {
    const toast = useToast();
    const { user } = useAuth();
    const [confirmState, setConfirmState] = useState({ open: false, title: '', message: '', onConfirm: null });
    const [coupons, setCoupons] = useState([]);
    const [categories, setCategories] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const initialForm = {
        code: '',
        discountType: 'percentage',
        discountValue: '',
        minOrderValue: '0',
        maxDiscountAmount: '',
        expiresAt: '',
        isActive: true,
        restrictionType: 'all', // 'all', 'category', 'item'
        applicableCategoryIds: [],
        applicableMenuItemIds: []
    };

    const [formData, setFormData] = useState(initialForm);

    useEffect(() => {
        if (user?.restaurant_id) {
            fetchCoupons();
            fetchOptions();
        }
    }, [user]);

    const fetchCoupons = async () => {
        const resId = user?.restaurant_id;
        if (!resId) return;
        try {
            setLoading(true);
            const res = await api.get(`/api/coupons?restaurantId=${resId}`);
            setCoupons(res.data || []);
        } catch (error) {
            console.error('Failed to fetch coupons', error);
            toast.error('Failed to load coupons');
        } finally {
            setLoading(false);
        }
    };

    const fetchOptions = async () => {
        const resId = user?.restaurant_id;
        if (!resId) return;
        try {
            const [catRes, itemRes] = await Promise.all([
                api.get(`/api/menu/categories?restaurantId=${resId}`),
                api.get(`/api/menu/items?admin=true&restaurantId=${resId}`)
            ]);
            setCategories(catRes.data || []);
            setMenuItems(itemRes.data || []);
        } catch (error) {
            console.error('Failed to fetch coupon restriction options', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            // Filter lists based on restriction type choice
            const finalCategories = formData.restrictionType === 'category' ? formData.applicableCategoryIds : [];
            const finalMenuItems = formData.restrictionType === 'item' ? formData.applicableMenuItemIds : [];

            const payload = {
                ...formData,
                discountValue: parseFloat(formData.discountValue),
                minOrderValue: parseFloat(formData.minOrderValue) || 0,
                maxDiscountAmount: formData.maxDiscountAmount ? parseFloat(formData.maxDiscountAmount) : null,
                expiresAt: formData.expiresAt || null,
                applicableCategoryIds: finalCategories,
                applicableMenuItemIds: finalMenuItems
            };

            if (isEditing) {
                await api.put(`/api/coupons/${editId}`, payload);
                toast.success('Coupon updated successfully');
            } else {
                await api.post('/api/coupons', payload);
                toast.success('Coupon created successfully');
            }

            setFormData(initialForm);
            setIsEditing(false);
            setEditId(null);
            setShowForm(false);
            fetchCoupons();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save coupon');
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleStatus = async (coupon) => {
        try {
            await api.put(`/api/coupons/${coupon.id}`, {
                isActive: !coupon.is_active
            });
            toast.success(`Coupon ${!coupon.is_active ? 'activated' : 'deactivated'}`);
            fetchCoupons();
        } catch (error) {
            toast.error('Failed to toggle coupon status');
        }
    };

    const handleEditClick = (coupon) => {
        let restrictionType = 'all';
        if (coupon.applicable_category_ids && coupon.applicable_category_ids.length > 0) {
            restrictionType = 'category';
        } else if (coupon.applicable_menu_item_ids && coupon.applicable_menu_item_ids.length > 0) {
            restrictionType = 'item';
        }

        setFormData({
            code: coupon.code,
            discountType: coupon.discount_type,
            discountValue: String(coupon.discount_value),
            minOrderValue: String(coupon.min_order_value || 0),
            maxDiscountAmount: coupon.max_discount_amount ? String(coupon.max_discount_amount) : '',
            expiresAt: coupon.expires_at ? new Date(coupon.expires_at).toISOString().split('T')[0] : '',
            isActive: coupon.is_active,
            restrictionType,
            applicableCategoryIds: coupon.applicable_category_ids || [],
            applicableMenuItemIds: coupon.applicable_menu_item_ids || []
        });
        setEditId(coupon.id);
        setIsEditing(true);
        setShowForm(true);
    };

    const handleDelete = (id) => {
        setConfirmState({
            open: true,
            title: 'Delete Coupon',
            message: 'Are you sure you want to delete this coupon? This action cannot be undone.',
            onConfirm: async () => {
                try {
                    await api.delete(`/api/coupons/${id}`);
                    toast.success('Coupon deleted successfully');
                    fetchCoupons();
                } catch (error) {
                    toast.error('Failed to delete coupon');
                } finally {
                    setConfirmState({ open: false, title: '', message: '', onConfirm: null });
                }
            }
        });
    };

    if (loading) return <Loading mode="page" message="Loading coupons..." />;

    return (
        <div className="space-y-6">
            <ConfirmModal
                isOpen={confirmState.open}
                title={confirmState.title}
                message={confirmState.message}
                confirmText="Delete"
                onConfirm={confirmState.onConfirm}
                onCancel={() => setConfirmState({ open: false, title: '', message: '', onConfirm: null })}
            />

            <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                <h1 className="text-xl font-bold text-gray-800">Manage Coupons & Offers</h1>
                {!showForm && (
                    <button onClick={() => setShowForm(true)} className="btn-primary flex items-center shadow-lg">
                        <Plus className="w-5 h-5 mr-2" />
                        Create Coupon
                    </button>
                )}
            </div>

            {showForm && (
                <div className="card p-6 border-2 border-primary-100">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-bold">{isEditing ? 'Edit Coupon' : 'Create New Coupon'}</h2>
                        <button
                            onClick={() => {
                                setShowForm(false);
                                setIsEditing(false);
                                setEditId(null);
                                setFormData(initialForm);
                            }}
                            className="text-gray-500 hover:text-gray-700 font-semibold"
                        >
                            Cancel
                        </button>
                    </div>
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Coupon Code *</label>
                            <input
                                type="text"
                                required
                                className="w-full px-4 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500 uppercase"
                                placeholder="E.g., SAVE20"
                                value={formData.code}
                                onChange={e => setFormData({ ...formData, code: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Discount Type *</label>
                            <select
                                className="w-full px-4 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500"
                                value={formData.discountType}
                                onChange={e => setFormData({ ...formData, discountType: e.target.value })}
                            >
                                <option value="percentage">Percentage (%)</option>
                                <option value="fixed">Fixed Amount (₹)</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Discount Value ({formData.discountType === 'percentage' ? '%' : '₹'}) *
                            </label>
                            <input
                                type="number"
                                required
                                min="0.01"
                                step="any"
                                className="w-full px-4 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500"
                                placeholder={formData.discountType === 'percentage' ? '20' : '150'}
                                value={formData.discountValue}
                                onChange={e => setFormData({ ...formData, discountValue: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Minimum Order Subtotal (₹)</label>
                            <input
                                type="number"
                                min="0"
                                step="any"
                                className="w-full px-4 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500"
                                value={formData.minOrderValue}
                                onChange={e => setFormData({ ...formData, minOrderValue: e.target.value })}
                            />
                        </div>

                        {formData.discountType === 'percentage' && (
                            <div>
                                <label className="block text-sm font-medium mb-1">Max Discount Cap (₹, Optional)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    placeholder="Leave blank for no limit"
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500"
                                    value={formData.maxDiscountAmount}
                                    onChange={e => setFormData({ ...formData, maxDiscountAmount: e.target.value })}
                                />
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium mb-1">Expiry Date (Optional)</label>
                            <input
                                type="date"
                                className="w-full px-4 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500"
                                value={formData.expiresAt}
                                onChange={e => setFormData({ ...formData, expiresAt: e.target.value })}
                            />
                        </div>

                        {/* Restrictions Selection */}
                        <div className="md:col-span-2 border-t pt-4 mt-2">
                            <label className="block text-sm font-semibold text-gray-800 mb-2">Coupon Restrictions</label>
                            <div className="flex space-x-6 mb-4">
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input 
                                        type="radio" 
                                        name="restrictionType" 
                                        value="all" 
                                        checked={formData.restrictionType === 'all'} 
                                        onChange={e => setFormData({ ...formData, restrictionType: e.target.value })} 
                                    />
                                    <span className="text-sm">Applies to All Items</span>
                                </label>
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input 
                                        type="radio" 
                                        name="restrictionType" 
                                        value="category" 
                                        checked={formData.restrictionType === 'category'} 
                                        onChange={e => setFormData({ ...formData, restrictionType: e.target.value })} 
                                    />
                                    <span className="text-sm">Limit to Categories</span>
                                </label>
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input 
                                        type="radio" 
                                        name="restrictionType" 
                                        value="item" 
                                        checked={formData.restrictionType === 'item'} 
                                        onChange={e => setFormData({ ...formData, restrictionType: e.target.value })} 
                                    />
                                    <span className="text-sm">Limit to Specific Items</span>
                                </label>
                            </div>

                            {formData.restrictionType === 'category' && (
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Select Applicable Categories</label>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {categories.map(c => {
                                            const isChecked = formData.applicableCategoryIds.includes(c.id);
                                            return (
                                                <label key={c.id} className="flex items-center space-x-2 text-sm bg-white p-2 rounded border hover:bg-gray-50 cursor-pointer">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={isChecked}
                                                        onChange={() => {
                                                            const ids = isChecked 
                                                                ? formData.applicableCategoryIds.filter(id => id !== c.id)
                                                                : [...formData.applicableCategoryIds, c.id];
                                                            setFormData({ ...formData, applicableCategoryIds: ids });
                                                        }}
                                                    />
                                                    <span className="truncate">{c.name}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {formData.restrictionType === 'item' && (
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Select Applicable Menu Items</label>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1">
                                        {menuItems.map(item => {
                                            const isChecked = formData.applicableMenuItemIds.includes(item.id);
                                            return (
                                                <label key={item.id} className="flex items-center space-x-2 text-sm bg-white p-2 rounded border hover:bg-gray-50 cursor-pointer">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={isChecked}
                                                        onChange={() => {
                                                            const ids = isChecked 
                                                                ? formData.applicableMenuItemIds.filter(id => id !== item.id)
                                                                : [...formData.applicableMenuItemIds, item.id];
                                                            setFormData({ ...formData, applicableMenuItemIds: ids });
                                                        }}
                                                    />
                                                    <span className="truncate">{item.name} <span className="text-gray-400 font-semibold">(₹{item.price})</span></span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center space-x-2 py-2">
                            <input
                                type="checkbox"
                                id="isActive"
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                checked={formData.isActive}
                                onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                            />
                            <label htmlFor="isActive" className="text-sm font-medium text-gray-700">Active and ready to use</label>
                        </div>

                        <div className="md:col-span-2 flex space-x-4 mt-2">
                            <button type="submit" disabled={submitting} className="btn-primary flex items-center min-w-[150px] justify-center">
                                {submitting ? (
                                    <Loading mode="button" message={isEditing ? 'Updating...' : 'Creating...'} />
                                ) : (
                                    <>
                                        {isEditing ? <Edit2 className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                                        {isEditing ? 'Update Coupon' : 'Create Coupon'}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {!showForm && (
                <div className="card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Restrictions</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Min Order</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expiry</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {coupons.map((coupon) => {
                                    const hasCats = coupon.applicable_category_ids && coupon.applicable_category_ids.length > 0;
                                    const hasItems = coupon.applicable_menu_item_ids && coupon.applicable_menu_item_ids.length > 0;
                                    
                                    return (
                                        <tr key={coupon.id}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="font-mono font-bold text-sm bg-gray-100 text-gray-800 px-2.5 py-1 rounded">
                                                    {coupon.code}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                                                {coupon.discount_type}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                                {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `₹${coupon.discount_value}`}
                                                {coupon.max_discount_amount && (
                                                    <div className="text-[10px] text-gray-500 font-normal">
                                                        Max: ₹{coupon.max_discount_amount}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                                                {hasCats && (
                                                    <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                                                        {coupon.applicable_category_ids.length} Categories
                                                    </span>
                                                )}
                                                {hasItems && (
                                                    <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full font-semibold">
                                                        {coupon.applicable_menu_item_ids.length} Items
                                                    </span>
                                                )}
                                                {!hasCats && !hasItems && (
                                                    <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                                                        Global (All Items)
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                ₹{coupon.min_order_value}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {coupon.expires_at ? new Date(coupon.expires_at).toLocaleDateString() : 'Never'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <button 
                                                    onClick={() => handleToggleStatus(coupon)}
                                                    className="flex items-center text-gray-500 hover:text-primary-600 transition-colors"
                                                >
                                                    {coupon.is_active ? (
                                                        <ToggleRight className="w-6 h-6 text-green-500" />
                                                    ) : (
                                                        <ToggleLeft className="w-6 h-6 text-gray-400" />
                                                    )}
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button onClick={() => handleEditClick(coupon)} className="text-indigo-600 hover:text-indigo-900 mr-4">
                                                    <Edit2 className="w-5 h-5" />
                                                </button>
                                                <button onClick={() => handleDelete(coupon.id)} className="text-red-600 hover:text-red-900">
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {coupons.length === 0 && (
                                    <tr>
                                        <td colSpan="8" className="text-center py-8 text-gray-500 text-sm">
                                            No coupons configured yet. Click 'Create Coupon' to add one.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
